// src/components/Footer/Footer.jsx

import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Mail, MapPin, Music4, Phone } from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { DASH_USER_QS } from "../../utils/nav";
import SocialLinks from "../Social/SocialLinks.jsx";
import ResponsiveButton from "../ResponsiveButton/NeonButton.jsx";

const BLOG_ROUTE = "/blogi";

const popularLinks = [
  { to: "/bilebandi-juhliin", label: "Bilebändi juhliin", icon: "🎤" },
  { to: "/dj-juhliin", label: "DJ juhliin", icon: "🎧" },
  { to: "/trubaduuri-juhliin", label: "Trubaduuri juhliin", icon: "🎸" },
  { to: "/valokuvaaja-juhliin", label: "Valokuvaaja juhliin", icon: "📸" },
  { to: "/duo-juhliin", label: "Duo juhliin", icon: "🎻" },
  { to: "/sooloartisti-juhliin", label: "Sooloartisti juhliin", icon: "🎙️" },
  { to: "/taikuri-juhliin", label: "Taikuri juhliin", icon: "🪄" },
  { to: "/juontaja-juhliin", label: "Juontaja juhliin", icon: "🎙️" },
  {
    to: "/standup-koomikko-juhliin",
    label: "Stand up -koomikko juhliin",
    icon: "😂",
  },
];

const serviceLinks = [
  { to: "/useinkysyttya", label: "UKK" },
  { to: "/esiintyjat-ja-palvelut", label: "Esiintyjät ja palvelut" },
  { to: BLOG_ROUTE, label: "Blogi" },
  { to: "/tietoameista", label: "Tietoa meistä" },
  { to: "/yhteistyo", label: "Yhteistyö & kumppanit" },
];

const regionLinks = [
  { to: "/trubaduuri-uusimaa", label: "Trubaduuri Uusimaa" },
  { to: "/trubaduuri-pirkanmaa", label: "Trubaduuri Pirkanmaa" },
  { to: "/bilebandi-varsinais-suomi", label: "Bilebändi Varsinais-Suomi" },
  { to: "/trubaduuri-paijat-hame", label: "Trubaduuri Päijät-Häme" },
  { to: "/trubaduuri-oulu", label: "Trubaduuri Oulu" },
  { to: "/valokuvaaja-kanta-hame", label: "Valokuvaaja Kanta-Häme" },
  { to: "/duo-satakunta", label: "Duo Satakunta" },
  { to: "/sooloartisti-kymenlaakso", label: "Sooloartisti Kymenlaakso" },
];

const titleClass =
  "font-customFont2 text-sm font-semibold uppercase tracking-[0.14em] text-black/70 dark:text-white/80";

const textClass =
  "font-customFont1 text-sm text-black/65 dark:text-white/65";

const linkClass =
  "font-customFont1 text-sm text-black/70 transition-colors hover:text-sky-700 dark:text-white/70 dark:hover:text-primary";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useAuth?.() || {};
  const { user, currentUser, token, isAuthenticated } = auth;
  const userLike = user ?? currentUser ?? null;

  const hasUserId = !!(
    userLike &&
    (userLike.id || userLike._id || userLike.user_id || userLike.email)
  );

  const isAuthed = Boolean(
    typeof isAuthenticated === "boolean"
      ? isAuthenticated
      : token || hasUserId
  );

  const norm = (p) => (p === "/" ? "/" : p.replace(/\/+$/, ""));
  const path = norm(location.pathname);

  const params = new URLSearchParams(location.search);

  const cameFromOnboarding =
    (params.get("from") || "").toLowerCase() === "onboarding" ||
    location.state?.fromOnboarding === true;

  const termsLikePaths = [
    "/kayttoehdot",
    "/ohjeet-ja-ehdot",
    "/tietosuoja",
    "/tietosuojaseloste",
  ];

  const onboardingRoots = [
    "/rekisteroidy",
    "/verify-email",
    "/onboarding",
    "/setup",
  ];

  const isOnboardingFlow =
    onboardingRoots.some((p) => path.startsWith(p)) ||
    (termsLikePaths.includes(path) && cameFromOnboarding);

  const canShowDashboard = isAuthed && !isOnboardingFlow;

  const goHomeTop = () => {
    navigate("/");

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, 50);
  };

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer
        id="site-footer"
        className="relative text-black dark:text-white"
      >
      {/* Yläreuna */}
      <div className="h-px bg-gradient-to-r from-transparent via-black/15 to-transparent dark:via-primary/60" />

      <div className="relative overflow-hidden bg-white dark:bg-gradient-to-b dark:from-[#090011] dark:to-[#050009]">
        <div className="relative mx-auto max-w-[1500px] px-4 py-8 sm:px-6 lg:px-8">
          {/* =====================================================
              KOMPAKTI CTA
          ===================================================== */}
          <section className="mb-9 overflow-hidden rounded-3xl border border-black/10 bg-white/45 dark:border-white/10 dark:bg-white/[0.04]">
            <div className="flex flex-col gap-5 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-7">
              <div>
                <h2 className="font-customFont2 text-xl font-semibold text-black/85 dark:text-white md:text-2xl">
                  Löydä sopiva tekijä tapahtumaasi
                </h2>

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-customFont1 text-xs text-black/60 dark:text-white/60 sm:text-sm">
                  <span>✓ Hinnat näkyvissä</span>
                  <span>✓ Vapaat ajat</span>
                  <span>✓ Ei sitoutumista</span>
                </div>
              </div>

              <div className="shrink-0">
                <ResponsiveButton
                  to="/"
                  variant="chip"
                  className="w-full sm:w-auto"
                  onClick={goHomeTop}
                >
                  Katso vapaat tekijät
                </ResponsiveButton>
              </div>
            </div>
          </section>

          {/* =====================================================
              PÄÄSISÄLTÖ
          ===================================================== */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 text-center sm:grid-cols-2 sm:text-left lg:grid-cols-12">
            {/* BUUKKERI / YHTEYS / SOME */}
            <section className="sm:col-span-2 lg:col-span-3">
              <div className="flex justify-center sm:justify-start">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-black/[0.055] dark:border-white/10 dark:bg-white/[0.06]">
                  <Music4
                    className="h-5 w-5 text-sky-700 dark:text-primary"
                    aria-hidden="true"
                  />
                </div>
              </div>

              <h3 className="mt-4 font-customFont2 text-2xl font-semibold tracking-tight text-black/85 dark:text-white">
                Buukkeri
              </h3>

              <p className="mx-auto mt-2 max-w-xs font-customFont1 text-sm leading-relaxed text-black/60 dark:text-white/60 sm:mx-0">
                Esiintyjät ja tapahtumapalvelut helposti yhdestä paikasta.
                Vertaa hintoja ja saatavuutta verkossa.
              </p>

              <div className="mt-5 space-y-2">
                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <MapPin
                    className="h-4 w-4 shrink-0 text-sky-700 dark:text-primary"
                    aria-hidden="true"
                  />
                  <span className={textClass}>Kerava, Uusimaa</span>
                </div>

                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <Phone
                    className="h-4 w-4 shrink-0 text-sky-700 dark:text-primary"
                    aria-hidden="true"
                  />
                  <a href="tel:+358443519265" className={linkClass}>
                    +358 44 351 92 65
                  </a>
                </div>

                <div className="flex items-center justify-center gap-2 sm:justify-start">
                  <Mail
                    className="h-4 w-4 shrink-0 text-sky-700 dark:text-primary"
                    aria-hidden="true"
                  />
                  <a href="mailto:info@buukkeri.fi" className={linkClass}>
                    info@buukkeri.fi
                  </a>
                </div>
              </div>

              <div
                className="
                  mt-5 flex justify-center sm:justify-start

                  [&_a]:!border-black/10
                  [&_a]:!bg-black/[0.055]
                  [&_a]:!text-black/80
                  [&_svg]:!text-black/80

                  dark:[&_a]:!border-white/10
                  dark:[&_a]:!bg-white/[0.06]
                  dark:[&_a]:!text-white/90
                  dark:[&_svg]:!text-white/90
                "
              >
                <SocialLinks variant="footer" />
              </div>
            </section>

            {/* SUOSITUIMMAT HAUT */}
            <section className="lg:col-span-2">
              <h3 className={titleClass}>Suosituimmat haut</h3>

              <ul className="mt-4 space-y-2.5">
                {popularLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className={`${linkClass} inline-flex items-start gap-2`}
                    >
                      <span
                        className="mt-[1px] shrink-0 text-xs"
                        aria-hidden="true"
                      >
                        {link.icon}
                      </span>

                      <span>{link.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>

            {/* PALVELU */}
            <section className="lg:col-span-2">
              <h3 className={titleClass}>Palvelu</h3>

              <ul className="mt-4 space-y-2.5">
                {serviceLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink to={link.to} className={linkClass}>
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>

            {/* PROFIILILLE */}
            <section className="lg:col-span-2">
              <h3 className={titleClass}>Profiileille</h3>

              <ul className="mt-4 space-y-2.5">
                <li>
                  <NavLink to="/liity-buukkeriin" className={linkClass}>
                    Liity Buukkeriin
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/ukk-profiileille" className={linkClass}>
                    Profiilien UKK
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/buukkeri-embed" className={linkClass}>
                    Buukkeri Embed
                  </NavLink>
                </li>

                <li>
                  <NavLink to="/paranoidi" className={linkClass}>
                    Paranoidi
                  </NavLink>
                </li>

                {canShowDashboard && (
                  <li>
                    <NavLink
                      to={`/dashboard?${DASH_USER_QS}`}
                      className={linkClass}
                    >
                      Hallintapaneeli
                    </NavLink>
                  </li>
                )}

                {!isAuthed && (
                  <>
                    <li>
                      <NavLink to="/kirjaudu" className={linkClass}>
                        Kirjaudu sisään
                      </NavLink>
                    </li>

                    <li>
                      <NavLink to="/rekisteroidy" className={linkClass}>
                        Luo tili
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>
            </section>

            {/* ALUEHAUT */}
            <section className="sm:col-span-2 lg:col-span-3">
              <h3 className={titleClass}>Aluehaut ympäri Suomen</h3>

              <p className="mt-2 font-customFont1 text-xs leading-relaxed text-black/50 dark:text-white/45">
                Suosittuja ja ajankohtaisia aluehakuja eri puolilta Suomea.
              </p>

              <div className="mt-4 grid grid-cols-1 gap-2 xl:grid-cols-2">
                {regionLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className="
                      group flex min-h-10 items-center gap-2 rounded-xl
                      border border-black/[0.08]
                      bg-white/35 px-3 py-2
                      font-customFont1 text-xs
                      text-black/70
                      transition-colors
                      hover:border-sky-500/30
                      hover:bg-white/65
                      hover:text-sky-700

                      dark:border-white/[0.08]
                      dark:bg-white/[0.035]
                      dark:text-white/65
                      dark:hover:border-primary/25
                      dark:hover:bg-white/[0.07]
                      dark:hover:text-primary
                    "
                  >
                    <span
                      className="
                        h-1.5 w-1.5 shrink-0 rounded-full
                        bg-sky-600/70
                        transition
                        group-hover:bg-sky-500

                        dark:bg-primary/65
                        dark:group-hover:bg-primary
                      "
                      aria-hidden="true"
                    />

                    <span className="leading-snug">{link.label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          </div>

          {/* =====================================================
              ALAPALKKI
          ===================================================== */}
          <div className="mt-9 border-t border-black/10 pt-5 dark:border-white/10">
            <div className="flex flex-col gap-4 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left">
              <div className="font-customFont1 text-xs text-black/55 dark:text-white/50">
                © {new Date().getFullYear()} Buukkeri · Y-tunnus 3214378-6
              </div>

              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-customFont1 text-xs text-black/55 dark:text-white/50">
                <NavLink
                  to="/tietosuoja"
                  className={linkClass}
                  onClick={scrollTop}
                >
                  Tietosuoja
                </NavLink>

                <span aria-hidden="true">·</span>

                <NavLink
                  to="/kayttoehdot"
                  className={linkClass}
                  onClick={scrollTop}
                >
                  Käyttöehdot
                </NavLink>

                <span aria-hidden="true">·</span>

                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(
                      new Event("buukkeri:open-cookie-settings")
                    );
                  }}
                  className={linkClass}
                >
                  Evästeasetukset
                </button>
              </div>

              <div className="font-customFont1 text-xs text-black/50 dark:text-white/45">
                Suunnittelu ja toteutus:{" "}
                <a
                  href="https://arttustenius.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 transition-colors hover:text-sky-700 dark:hover:text-primary"
                >
                  Arttu Stenius
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;